import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { userApiClient } from '../api/HttpClient';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

function getFirebaseApp() {
  if (getApps().length > 0) return getApps()[0];
  return initializeApp(firebaseConfig);
}

async function registerTokenWithBackend(token: string) {
  try {
    const client = await userApiClient;
    await client.post('/api/pg_tenant/fcm-token/', { token, platform: 'web' });
  } catch {
    // Non-fatal — backend may not be available
  }
}

async function removeTokenFromBackend(token: string) {
  try {
    const client = await userApiClient;
    await client.delete('/api/pg_tenant/fcm-token/', { data: { token } } as any);
  } catch {
    // Non-fatal
  }
}

type MessageCallback = (title: string, body: string) => void;
const foregroundListeners: MessageCallback[] = [];

export function subscribeToForegroundMessages(cb: MessageCallback): () => void {
  foregroundListeners.push(cb);
  return () => {
    const idx = foregroundListeners.indexOf(cb);
    if (idx !== -1) foregroundListeners.splice(idx, 1);
  };
}

export async function initNotifications(): Promise<void> {
  if (!firebaseConfig.apiKey) return;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

  try {
    const app = getFirebaseApp();
    const messaging = getMessaging(app);

    const permission = await Notification.requestPermission();
    console.log('[FCM] Notification permission:', permission);
    if (permission !== 'granted') return;

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('[FCM] Service worker registered:', registration.scope);

    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    console.log('[FCM] Token:', token ? token.slice(0, 20) + '...' : 'null');
    if (token) {
      await registerTokenWithBackend(token);
      console.log('[FCM] Token registered with backend.');
    }

    onMessage(messaging, (payload: MessagePayload) => {
      const { title, body } = payload.notification || {};
      if (!title) return;
      foregroundListeners.forEach(cb => cb(title, body || ''));
    });
  } catch (err) {
    console.error('[FCM] Push notification setup failed:', err);
  }
}

export async function revokeNotifications(): Promise<void> {
  if (!firebaseConfig.apiKey) return;
  try {
    const app = getFirebaseApp();
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await removeTokenFromBackend(token);
    }
  } catch {
    // Non-fatal
  }
}
